import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Check, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { formatYearMonth } from '@/lib/datetime';
import { aneelCaseParams, aneelFeeParams, fetchAneelEntity } from '@/services/aneel';
import type {
  AneelFeeCreditExposure,
  AneelFineCollection,
  AneelTrackPresence,
} from '@/services/types';

/**
 * One company across all four ANEEL tracks.
 *
 * The only screen in the product that puts an enforcement corpus, two
 * non-infraction instruments and a fee ledger on one page, which is exactly why
 * every section states what it is before it states a number. Two rules the
 * layout enforces:
 *
 * - **Reach renders first.** Most of this page is empty for most companies
 *   (2,675 of 3,803 appear in exactly one track), so the reader is told which
 *   tracks exist before meeting a section that has nothing in it.
 * - **`cnpj_valid` is never a badge.** The ANATEL profile renders that flag as
 *   verified/unverified; here it is TRUE on exactly the companies holding an
 *   infraction notice and unset on the other 2,736, so a badge would mark 72%
 *   of the corpus as suspect on the strength of which loader wrote the row.
 */
export function AneelEntityProfilePage() {
  const { t, i18n } = useTranslation(['aneel', 'common']);
  const { cnpj = '' } = useParams();

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['aneel', 'entity', cnpj],
    queryFn: () => fetchAneelEntity(cnpj),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={3} />
        <SkeletonLoader rows={8} />
      </div>
    );
  }

  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-4 p-6">
        <BackLink />
        <ErrorMessage
          message={notFound ? t('aneel:entity.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

  const name = data.reach.value?.canonical_name ?? cnpj;

  return (
    <div className="space-y-4 p-6">
      <BackLink />

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-lg font-semibold text-foreground">{name}</h1>
          <p className="text-sm tabular-nums text-muted-foreground" data-cnpj={cnpj}>
            {t('aneel:entity.cnpjLabel')} {formatCnpj(cnpj)}
          </p>
          {/* The two lists this profile summarises, opened narrowed to this
              company by the filters those lists already offer. */}
          <p className="flex flex-wrap gap-x-3 text-xs">
            <Link
              to={`/aneel/cases?${aneelCaseParams({ entity: cnpj }).toString()}`}
              className="text-primary underline-offset-4 hover:underline"
              data-all-cases
            >
              {t('aneel:entity.allCases')}
            </Link>
            <Link
              to={`/aneel/fees?${aneelFeeParams({ query: cnpj }).toString()}`}
              className="text-primary underline-offset-4 hover:underline"
              data-fee-ledger
            >
              {t('aneel:entity.feeLedger')}
            </Link>
          </p>
        </div>
        {/* The three case tracks as rows; the fee ledger stays in the preamble,
            because a fee row beside a penalty row in one file is the mistake
            the whole page is arranged to prevent. */}
        <ExportButton path={`/api/v1/aneel/export/e/${encodeURIComponent(cnpj)}`} />
      </header>

      <ExplainerCard i18nKey="aneel:entity.explainer" />

      <AsOfStrip asOf={data.as_of} />

      {/* ---------------------------------------------------------------- */}
      {/* Reach -- rendered before any track, so an empty section below is  */}
      {/* read as an absence that was announced rather than a failed load.  */}
      {/* ---------------------------------------------------------------- */}

      <section className="space-y-3 rounded-lg border bg-card p-4">
        <h2 className="text-sm font-semibold text-foreground">{t('aneel:entity.reach.title')}</h2>

        <Answer answer={data.reach} help={t('aneel:entity.reach.help')}>
          {(reach) => (
            <div className="space-y-3">
              <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {reach.tracks.map((track) => (
                  <TrackCard key={track.track} track={track} />
                ))}
              </ul>

              {/* The commonest shape in the corpus: 2,375 of 3,803 companies
                  appear only because they are billed a levy. Without this the
                  page reads as an enforcement profile that happens to be
                  empty, which is a claim about the company. */}
              {reach.fee_only ? (
                <p
                  role="note"
                  className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-foreground"
                >
                  {t('aneel:entity.reach.feeOnly')}
                </p>
              ) : null}

              <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                {t('aneel:entity.reach.neverOneTotal')}
              </p>

              {/* Provenance, never a verdict. Nothing at all is said when the
                  flag is unset, because "unverified" would be the same false
                  claim in the other direction. */}
              {reach.cnpj_valid !== null ? (
                <p className="text-xs text-muted-foreground">
                  {t('aneel:entity.reach.cnpjFlag')}
                </p>
              ) : null}
            </div>
          )}
        </Answer>

        <FieldGuide i18nKey="aneel:entity.guide.reach" />
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Enforcement                                                       */}
      {/* ---------------------------------------------------------------- */}

      <section className="space-y-3 rounded-lg border bg-card p-4">
        <h2 className="text-sm font-semibold text-foreground">
          {t('aneel:entity.enforcement.title')}
        </h2>

        {/* scope="record": these refuse because the COMPANY is absent from the
            track, not because ANEEL's corpus cannot support it. The default
            badge would read "unavailable for this agency". */}
        <Answer
          answer={data.enforcement}
          help={t('aneel:entity.enforcement.help')}
          scope="record"
        >
          {(history) => (
            <div className="space-y-3">
              <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Figure
                  label={t('aneel:entity.enforcement.cases')}
                  value={number.format(history.cases)}
                />
                <Figure
                  label={t('aneel:entity.enforcement.decided')}
                  value={number.format(history.decided)}
                />
                <Figure
                  label={t('aneel:entity.enforcement.charged')}
                  value={currency.format(history.charged_total)}
                />
                <Figure
                  label={t('aneel:entity.enforcement.movement')}
                  value={`${number.format(history.reduced)} / ${number.format(history.maintained)} / ${number.format(history.eliminated)} / ${number.format(history.increased)}`}
                />
              </dl>

              {/* The trap this whole section is arranged around: every
                  undecided case carries final == charged, so one combined
                  total would be mostly charges wearing an outcome's label. */}
              <p
                role="note"
                className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-foreground"
              >
                {t('aneel:entity.enforcement.chargedNotFinal', {
                  pending: number.format(history.pending),
                  cases: number.format(history.cases),
                })}
              </p>

              {history.decided > 0 ? (
                <div className="rounded-md border bg-background p-3">
                  <h3 className="mb-2 text-xs font-medium text-foreground">
                    {t('aneel:entity.enforcement.decidedTitle', {
                      decided: number.format(history.decided),
                    })}
                  </h3>
                  <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <Figure
                      label={t('aneel:entity.enforcement.decidedCharged')}
                      value={currency.format(history.decided_charged_total)}
                    />
                    <Figure
                      label={t('aneel:entity.enforcement.decidedFinal')}
                      value={currency.format(history.decided_final_total)}
                    />
                    <Figure
                      label={t('aneel:entity.enforcement.relief')}
                      value={currency.format(history.decided_relief)}
                    />
                  </dl>
                </div>
              ) : null}
            </div>
          )}
        </Answer>

        {data.enforcement_rows.length > 0 ? (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">
                    {t('aneel:entity.enforcement.columns.notice')}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t('aneel:entity.enforcement.columns.date')}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t('aneel:entity.enforcement.columns.nature')}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t('aneel:entity.enforcement.columns.agency')}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t('aneel:entity.enforcement.columns.trajectory')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('aneel:entity.enforcement.columns.charged')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('aneel:entity.enforcement.columns.standing')}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t('aneel:entity.enforcement.columns.closed')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.enforcement_rows.map((row) => (
                  <tr key={row.notice_number} className="border-t align-top">
                    <td className="px-3 py-2">
                      <Link
                        to={`/aneel/c/${encodeURIComponent(row.case_key)}`}
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {row.notice_number}
                      </Link>
                    </td>
                    <td className="px-3 py-2 tabular-nums text-muted-foreground">
                      {row.notice_date ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{row.nature ?? '—'}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {row.inspecting_agency ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {/* One vocabulary across the product: the same key the
                          case list uses. An `aneel:trajectory.*` namespace of
                          my own rendered the raw PENDING_DECISION enum. */}
                      {row.trajectory
                        ? t(`aneel:overview.trajectories.${row.trajectory}`, row.trajectory)
                        : t('aneel:entity.enforcement.undecided')}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-foreground">
                      {row.charged_amount === null ? '—' : currency.format(row.charged_amount)}
                    </td>
                    {/* Labelled per row: on an undecided case this equals the
                        charge by construction and is not an outcome. */}
                    <td
                      className="px-3 py-2 text-right tabular-nums"
                      data-standing
                      data-outcome={String(row.trajectory !== null && row.trajectory !== 'PENDING_DECISION')}
                    >
                      {row.final_amount === null ? (
                        '—'
                      ) : row.trajectory !== null && row.trajectory !== 'PENDING_DECISION' ? (
                        <span className="text-foreground">{currency.format(row.final_amount)}</span>
                      ) : (
                        <span className="text-muted-foreground" title={t('aneel:cases.chargedNotFinal')}>
                          {currency.format(row.final_amount)}{' '}
                          <span className="text-[0.65rem] uppercase tracking-wide">
                            {t('aneel:cases.chargedShort')}
                          </span>
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-muted-foreground">
                      {row.closure_date ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        <FieldGuide i18nKey="aneel:entity.guide.enforcement" />
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Fine collection -- how the charges were collected, never a verdict */}
      {/* ---------------------------------------------------------------- */}

      <section className="space-y-3 rounded-lg border bg-card p-4" data-fine-collection>
        <h2 className="text-sm font-semibold text-foreground">
          {t('aneel:entity.fineCollection.title')}
        </h2>
        <Answer
          answer={data.fine_collection}
          help={t('aneel:entity.fineCollection.help')}
          scope="record"
        >
          {(collection) => <FineCollectionPanel collection={collection} />}
        </Answer>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Intimations and edital penalties                                  */}
      {/* ---------------------------------------------------------------- */}

      <section className="space-y-3 rounded-lg border bg-card p-4">
        <h2 className="text-sm font-semibold text-foreground">{t('aneel:entity.other.title')}</h2>
        <p className="text-xs text-muted-foreground">{t('aneel:entity.other.fence')}</p>

        <div className="grid gap-3 lg:grid-cols-2">
          <TrackList
            title={t('aneel:entity.other.tiTitle')}
            empty={t('aneel:entity.other.tiEmpty')}
            rows={data.ti_rows.map((row) => ({
              key: row.intimation_number,
              primary: row.intimation_number,
              secondary: [row.intimation_date ?? '—', row.nature].filter(Boolean).join(' · '),
              detail: row.penalty_types.join('; ') || '—',
              outcome: [
                row.manifestation_outcome ?? t('aneel:entity.other.noOutcome'),
                row.closure_date
                  ? t('aneel:entity.other.closedOn', { date: row.closure_date })
                  : null,
              ]
                .filter(Boolean)
                .join(' · '),
            }))}
          />
          <TrackList
            title={t('aneel:entity.other.tipeTitle')}
            empty={t('aneel:entity.other.tipeEmpty')}
            /* Row grain, not case grain: this track writes each stage as its
               own row, so one edital number can appear several times. */
            rows={data.tipe_rows.map((row, index) => ({
              key: `${row.edital_penalty_number}-${index}`,
              primary: row.edital_penalty_number ?? '—',
              secondary: row.inspection_process_number ?? '—',
              detail: row.penalty_type ?? '—',
              outcome: [
                row.original_fine_amount === null
                  ? '—'
                  : currency.format(row.original_fine_amount),
                row.fine_after_manifestation === null
                  ? null
                  : t('aneel:entity.other.afterManifestation', {
                      amount: currency.format(row.fine_after_manifestation),
                    }),
              ]
                .filter(Boolean)
                .join(' → '),
            }))}
          />
        </div>

        <FieldGuide i18nKey="aneel:entity.guide.other" />
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Fee ledger -- fenced hardest, because it is not a sanction        */}
      {/* ---------------------------------------------------------------- */}

      <section className="space-y-3 rounded-lg border bg-card p-4">
        <h2 className="text-sm font-semibold text-foreground">{t('aneel:entity.fees.title')}</h2>
        <p
          role="note"
          className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-foreground"
        >
          {t('aneel:entity.fees.fence')}
        </p>

        <Answer answer={data.fees} help={t('aneel:entity.fees.help')} scope="record">
          {(fees) => (
            <div className="space-y-3">
              <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Figure label={t('aneel:entity.fees.rows')} value={number.format(fees.rows)} />
                <Figure label={t('aneel:entity.fees.paid')} value={number.format(fees.paid)} />
                <Figure
                  label={t('aneel:entity.fees.pending')}
                  value={number.format(fees.pending)}
                />
                <Figure
                  label={t('aneel:entity.fees.billed')}
                  value={currency.format(fees.billed_total)}
                />
              </dl>
              <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                {t('aneel:entity.fees.span', {
                  earliest: formatYearMonth(fees.earliest),
                  latest: formatYearMonth(fees.latest),
                })}
              </p>
            </div>
          )}
        </Answer>

        {/* INSIDE the fee fence, deliberately: a debt-register entry for a
            levy is a collection state of the fee and must never sit beside an
            enforcement figure. Record scope: an absence is this company's. */}
        <div className="rounded-md border bg-background p-3" data-fee-exposure>
          <h3 className="mb-2 text-xs font-medium text-foreground">
            {t('aneel:entity.feeExposure.title')}
          </h3>
          <Answer
            answer={data.fee_exposure}
            help={t('aneel:entity.feeExposure.help')}
            scope="record"
          >
            {(rows) => <FeeExposurePanel rows={rows} feeRows={data.fees.value?.rows ?? 0} />}
          </Answer>
        </div>

        <FieldGuide i18nKey="aneel:entity.guide.fees" />
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Related companies                                                 */}
      {/* ---------------------------------------------------------------- */}

      <section className="space-y-3 rounded-lg border bg-card p-4">
        <h2 className="text-sm font-semibold text-foreground">{t('aneel:entity.related.title')}</h2>

        <Answer answer={data.related} help={t('aneel:entity.related.help')} scope="record">
          {(related) => (
            <ul className="space-y-2">
              {related.map((company) => (
                <li key={company.cnpj_digits} className="text-sm">
                  <Link
                    to={`/aneel/e/${company.cnpj_digits}`}
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    {company.canonical_name}
                  </Link>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {company.relationship_type}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Answer>
      </section>
    </div>
  );
}

/** `XX.XXX.XXX/YYYY-ZZ`, the way a CNPJ is read; the digits stay in `data-cnpj`. */
function formatCnpj(digits: string): string {
  if (digits.length !== 14) return digits;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

function FineCollectionPanel({ collection }: { collection: AneelFineCollection }) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Figure
          label={t('aneel:entity.fineCollection.rows')}
          value={number.format(collection.rows)}
        />
        <Figure
          label={t('aneel:entity.fineCollection.late')}
          value={`${number.format(collection.late_rows)} ${t('aneel:entity.fineCollection.lateOf', {
            n: number.format(collection.with_days_late),
          })}`}
        />
        <Figure
          label={t('aneel:entity.fineCollection.due')}
          value={currency.format(collection.due_total)}
        />
        <Figure
          label={t('aneel:entity.fineCollection.paid')}
          value={currency.format(collection.paid_total)}
        />
      </dl>
      <p className="text-xs text-muted-foreground" data-linked-rows>
        {t('aneel:entity.fineCollection.linked', {
          linked: number.format(collection.linked_rows),
          rows: number.format(collection.rows),
        })}
      </p>
      <div className="rounded-md border bg-background p-3">
        <h3 className="mb-2 text-xs font-medium text-foreground">
          {t('aneel:entity.fineCollection.statuses')}
        </h3>
        <ul className="space-y-1">
          {collection.statuses.map((row) => (
            <li
              key={row.status}
              data-fine-status={row.status}
              className="flex items-baseline justify-between gap-3 border-b pb-1 text-xs last:border-b-0"
            >
              <span className="text-foreground">{row.status}</span>
              <span className="tabular-nums text-muted-foreground">{number.format(row.rows)}</span>
            </li>
          ))}
        </ul>
        {collection.suspended_rows > 0 ? (
          <p className="mt-2 text-xs text-muted-foreground" data-suspended-rows>
            {t('aneel:entity.fineCollection.suspended')}: {number.format(collection.suspended_rows)}{' '}
            — {t('aneel:entity.fineCollection.suspendedHint')}
          </p>
        ) : null}
      </div>
      {collection.lateness ? (
        <div className="rounded-md border bg-background p-3" data-lateness>
          <h3 className="mb-2 text-xs font-medium text-foreground">
            {t('aneel:entity.fineCollection.lateness')}
          </h3>
          <dl className="grid grid-cols-3 gap-3 sm:grid-cols-5">
            {Object.entries(collection.lateness.quantiles).map(([key, value]) => (
              <div key={key}>
                <dt className="text-xs text-muted-foreground">{key}</dt>
                <dd className="text-sm tabular-nums text-foreground">{number.format(Math.round(value))}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : collection.late_rows > 0 ? (
        <p className="text-xs text-muted-foreground" data-lateness-floor>
          {t('aneel:entity.fineCollection.latenessFloor', { minimum: 20 })}
        </p>
      ) : null}
    </div>
  );
}

function FeeExposurePanel({ rows, feeRows }: { rows: AneelFeeCreditExposure[]; feeRows: number }) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language);

  if (rows.length === 0) {
    return (
      <p className="text-xs text-muted-foreground" data-fee-exposure-none>
        {t('aneel:entity.feeExposure.none', { rows: number.format(feeRows) })}
      </p>
    );
  }
  return (
    <table className="w-full text-xs">
      <thead className="text-left text-muted-foreground">
        <tr>
          <th className="py-1 font-medium">{t('aneel:entity.feeExposure.columns.status')}</th>
          <th className="py-1 text-right font-medium">{t('aneel:entity.feeExposure.columns.rows')}</th>
          <th className="py-1 font-medium">{t('aneel:entity.feeExposure.columns.span')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.status} className="border-t" data-fee-exposure-row={row.status}>
            <td className="py-1 text-foreground">{row.status}</td>
            <td className="py-1 text-right tabular-nums text-foreground">{number.format(row.rows)}</td>
            <td className="py-1 tabular-nums text-muted-foreground">
              {formatYearMonth(row.earliest)} — {formatYearMonth(row.latest)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * One track, present or absent.
 *
 * An absent track renders as a stated absence rather than being omitted: a
 * missing card is indistinguishable from one nobody built, and for 2,675 of
 * 3,803 companies three of these four are absent.
 */
function TrackCard({ track }: { track: AneelTrackPresence }) {
  const { t } = useTranslation(['aneel']);

  return (
    <li
      data-track={track.track}
      data-present={track.present}
      className={
        track.present
          ? 'rounded-md border bg-background p-3'
          : 'rounded-md border border-dashed bg-muted/30 p-3'
      }
    >
      <div className="flex items-center gap-1.5">
        {track.present ? (
          <Check className="h-3.5 w-3.5 text-foreground" aria-hidden />
        ) : (
          <Minus className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        )}
        <span className="text-xs font-medium text-foreground">
          {t(`aneel:entity.tracks.${track.track}`)}
        </span>
      </div>
      <p className="mt-1 text-sm tabular-nums text-muted-foreground">
        {track.present
          ? t(`aneel:entity.tracks.${track.track}Count`, { count: track.records })
          : t('aneel:entity.tracks.absent')}
      </p>
    </li>
  );
}

interface TrackRow {
  key: string;
  primary: string;
  secondary: string;
  detail: string;
  outcome: string;
}

function TrackList({ title, empty, rows }: { title: string; empty: string; rows: TrackRow[] }) {
  return (
    <div className="rounded-md border bg-background p-3">
      <h3 className="mb-2 text-xs font-medium text-foreground">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">{empty}</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((row) => (
            <li key={row.key} className="border-b pb-2 text-xs last:border-b-0 last:pb-0">
              <p className="tabular-nums text-foreground">{row.primary}</p>
              <p className="text-muted-foreground">
                {row.secondary} · {row.detail} · {row.outcome}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-background p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">{value}</dd>
    </div>
  );
}

function BackLink() {
  const { t } = useTranslation(['aneel']);
  return (
    <Link
      to="/aneel/entities"
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {t('aneel:entity.backToDirectory')}
    </Link>
  );
}
